import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { utcGunSaat } from '../utcTarih';
import { lmcMetni, lmcNetMetni, birimli, sureMetni, raporAgirlikSatirlari, altnWxrSatiri } from './inputFormat';
import { ofpMinKalkisYakiti, yakitAlimOncesi, asgariUstuYakit } from './ofpYakit';
import { directListesi } from './directTo';
import { usePencereKapat, KapatX } from './pencereKapat';   // pencere kapatma tek tip — ESC + ✕ (12 Eki 2026)
import { okumaDenetimi } from './kesikOkuma';   // okunamadı ≠ yok (21 Eki 2026)
import OkumaHatasi from './OkumaHatasi';

/** ISO UTC -> "19:06Z"; okunamazsa "--:--Z" (saat uydurulmaz). */
const saatZ = (iso) => {
  const t = iso ? new Date(iso) : null;
  if (!t || isNaN(t.getTime())) return '--:--Z';
  return `${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')}Z`;
};

function toMins(t) {
  if (!t || !t.includes(':')) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}
/** Görev süresi (DT) dakika: rapordan görev sonuna; okunamazsa null (uydurulmaz). */
const gorevSuresiDk = (g) => {
  const a = Date.parse(g?.report_time || ''), b = Date.parse(g?.duty_end || '');
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? Math.round((b - a) / 60000) : null;
};
function diffMins(a, b) {
  const am = toMins(a), bm = toMins(b);
  if (am === null || bm === null) return null;
  let d = bm - am; if (d < 0) d += 1440; return d;
}
// ════════════════════════════════════════════════════════════════════════
// 🔴 K-2 KAPANDI (31 Ağu 2026) — İKİNCİ FDP HESABI SİLİNDİ.
//
// Burada `getMaxFDP` adında ikinci bir hesap vardı: EASA tablosu KODA GÖMÜLÜ,
// `ftl_rulesets`'e hiç bağlı değil. Operatör SHT'ye geçtiğinde (Serkan: *"biz
// SHT takip edeceğiz"*) bu satırlar değişmedi — çünkü hiçbir yere bağlı değildi.
// Sonuç: aynı uçuş için FTL panelinde bir sayı, bu ekranda başka bir sayı.
//
// 31 Ağu'da ölçüldü (günün tamamı taranarak):
//   · rapor 15:00 · 4 sektör → ekran 11:00, gerçek 13:30  = 150 dk DAR
//     (yasal uçuşa kırmızı ⚠ basıyordu — yanlış alarm)
//   · rapor 05:00 · 1 sektör → ekran 12:00, gerçek 11:30  = 30 dk GEVŞEK
//     (gerçek aşım yeşil ✓ görünüyordu — bu tarafı daha tehlikeli)
// Ayrıca `sectorCount = 1` sabitti ve `minRest = max(fdp, 600/720)` gömülüydü
// (27 Ağu'da gerçek motordan söktüğümüz "mandal" kusurunun kopyası).
//
// 🔑 ÇÖZÜM "SHT'den hesapla" DEĞİL, "HİÇ HESAPLAMA": rapor artık `crew_duties`'e
// arşivlenirken YAZILMIŞ değeri gösterir. Sunucu tarafındaki PDF 6 Ağu'da zaten
// bu yola geçmişti (archive-flight); ekran tarafı unutulmuştu. İki motor kaldığı
// sürece bir gün yine ayrışırlar — hesap tek yerde kalınca ayrışamazlar (İlke 3).
//
// Kayıt yoksa SAYI ÜRETİLMEZ: "NO DUTY RECORD" yazar (İlke 1).
// ════════════════════════════════════════════════════════════════════════

// raporPdfBaglantisi — arsivlenmis ucus raporunun IMZALI baglantisi. TEK KAYNAK.
//
// Hem pilotun rapor ekrani hem admin panelin ARCHIVED FLTS detayi bunu cagirir.
// Ikinci bir kopya cikarilsaydi, biri `section` adini ya da kovayi degistirdiginde
// digeri SESSIZCE bozulurdu (Ilke 3) — rapor bulunamaz, sebebi de gorunmezdi.
//
// mode 'download' → tarayici dosyayi indirir; 'view' → sekmede acar.
// Rapor YOKSA null doner (hata degil): arsivlenmemis ucusta rapor da yoktur,
// bunu cagiran kendi dilinde soyler.
export async function raporPdfBaglantisi(planId, mode = 'download') {
  const { data: rows, error } = await supabase
    .from('efb_documents')
    .select('file_name,file_path')
    .eq('plan_id', planId)
    .eq('section', 'REPORT')
    .order('uploaded_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  if (!rows || !rows.length) return null;
  const { data: signed, error: sErr } = await supabase.storage
    .from('efb-documents')
    .createSignedUrl(rows[0].file_path, 3600,
      mode === 'download' ? { download: rows[0].file_name } : {});
  if (sErr) throw sErr;
  return { url: signed.signedUrl, fileName: rows[0].file_name };
}

export default function FlightReport({ plan, onClose }) {
  const [logs,        setLogs]        = useState([]);
  const [pfHomeBase,  setPfHomeBase]  = useState(null);
  const [pmHomeBase,  setPmHomeBase]  = useState(null);
  const [fltReport,   setFltReport]   = useState(null);
  const [duties,      setDuties]      = useState([]);   // crew_duties — KAYITLI FTL
  const [af,          setAf]          = useState(null); // archived_flights — surum + gecersizlik
  const [signedUrls,  setSignedUrls]  = useState({});
  const [linkHatasi,  setLinkHatasi]  = useState(null);   // imza/belge bağlantısı üretilemedi (21 Eki 2026)
  // DÜZELTME KATMANI (28 Tem) — admin_edits'ten, raporun okumasıyla birlikte gelir (21 Eki 2026; açıklaması aşağıda).
  const [amendments,  setAmendments]  = useState([]);
  // MIN T/O FUEL — OFP'nin aktif bacağından (12 Eki 2026, PDF ile aynı okuyucu); okunamazsa null.
  const [minTo,       setMinTo]       = useState(null);
  const [loading,     setLoading]     = useState(true);

  // 🔴 21 Eki 2026 (W4b): okumaların hiçbiri hatayı okumuyordu — düşen okuma raporu YARIM çiziyordu (FUEL "—", CREW DUTY
  //    TIMES "Not linked", hatta DELETED rozeti düşebiliyordu). Artık biri düşerse rapor çizilmez: kutu + RETRY.
  const [raporHatasi, setRaporHatasi] = useState(null);
  const [raporDeneme, setRaporDeneme] = useState(0);
  useEffect(() => {
    if (!plan?.id) return;
    const { dustu, oku } = okumaDenetimi();
    const al = (ad) => (r) => oku(ad, r) || [];
    setLoading(true);
    Promise.all([
      supabase.from('flight_logs').select('action,details,created_at').eq('plan_id', plan.id).order('created_at',{ascending:true}).then(al('FLIGHT LOG (flight_logs)')),
      supabase.from('flt_report').select('*').eq('plan_id', plan.id).limit(1).then(al('FLIGHT REPORT (flt_report)')),
      // K-2: FTL sayilari HESAPLANMAZ, arsivlenirken yazilan kayittan okunur.
      // 🔴 22 Eyl 2026: inis sayisi ve gece/gunduz de BURADAN. Ikisi de
      //    `archived_flights`ta olculur; `flt_report`ta YOKTUR. Bu ekran onlari
      //    `flt_report`tan okuyordu → gece/gunduz rozeti hic cikmiyordu, inis
      //    sayisi hic yoktu. Rapor PDF'iyle ayni kaynak (Ilke 3).
      supabase.from('archived_flights')
        .select('app_version,ios_version,device_model,deleted_at,delete_reason,landing_count,lnd_type,is_night_takeoff,is_night_landing')
        .eq('plan_id', plan.id).limit(1).then(al('ARCHIVE RECORD (archived_flights)')),
      supabase.from('crew_duties')
        .select('pilot_id,report_time,duty_end,fdp_minutes,max_fdp_minutes,fdp_exceeded,min_rest_minutes,earliest_next_report,operation_type,sectors')
        .contains('plan_ids', [plan.id]).then(al('CREW DUTIES (crew_duties)')),
      // OFP metni + aktif bacak — EXTRA FUEL (vs MIN T/O) için (12 Eki 2026).
      supabase.from('plan_versions').select('raw_text').eq('plan_id', plan.id)
        .order('created_at',{ascending:false}).limit(1).then(al('OFP TEXT (plan_versions)')),
      supabase.from('plans').select('fms_ident').eq('id', plan.id).limit(1).then(al('PLAN (plans)')),
      // Düzeltme katmanı da aynı okumada (eskiden ayrı, hatası okunmayan bir okumaydı — düşünce düzeltmeler görünmüyordu).
      supabase.from('admin_edits').select('field_name,old_value,new_value,reason,created_at')
        .eq('plan_id', plan.id).eq('edit_type', 'EDIT').order('created_at', { ascending: true }).then(al('AMENDMENTS (admin_edits)')),
    ]).then(([logsData, fltReportData, afData, dutyData, rawData, planData, duzeltmeler]) => {
      if (dustu.length) { setRaporHatasi(dustu); setLoading(false); return; }
      setRaporHatasi(null);
      setAmendments(duzeltmeler);
      setMinTo(ofpMinKalkisYakiti(rawData?.[0]?.raw_text, planData?.[0]?.fms_ident ?? plan.fms_ident));
      setAf(afData?.[0] || null);
      setDuties(dutyData || []);
      setFltReport(fltReportData?.[0] || null);
      setLogs(logsData || []);
      // PF ve PM pilot ID'lerini CREW_ASSIGNED log'undan al
      const crewLog = (logsData||[]).filter(l=>l.action==='CREW_ASSIGNED').slice(-1)[0];
      const pfId = crewLog?.details?.pf_id;
      const pmId = crewLog?.details?.pm_id;
      // PF ve PM home base'lerini çek
      const ub = okumaDenetimi();
      const fetchHB = (id) => id
        ? supabase.from('home_bases').select('icao').eq('pilot_id', id).limit(1)
            .then(r => ub.oku('HOME BASE (home_bases)', r) || [], e => ub.oku('HOME BASE (home_bases)', { error: e }) || [])
        : Promise.resolve([]);
      Promise.all([fetchHB(pfId), fetchHB(pmId)]).then(([pfHB, pmHB]) => {
        if (ub.dustu.length) { setRaporHatasi(ub.dustu); return; }
        setPfHomeBase(pfHB?.[0]?.icao || null);
        setPmHomeBase(pmHB?.[0]?.icao || null);
      });
      setLoading(false);
    }, (e) => { setRaporHatasi([{ ad: 'REPORT DATA', metin: e?.message || String(e) }]); setLoading(false); });
  }, [plan?.id, raporDeneme]); // eslint-disable-line

  // ── DIRECT TO LISTESI (22 Eyl 2026) — iPad aynasindan, PDF ile AYNI okuyucu
  // (`directTo.js`). Okunamazsa SUSMAZ: kartta yazar (Ilke 2).
  const [directler, setDirectler] = useState([]);
  const [directOkunamadi, setDirectOkunamadi] = useState(false);
  useEffect(() => {
    if (!plan?.id) return;
    supabase.from('navlog_data').select('direct_to').eq('plan_id', plan.id).limit(1)
      .then(({ data, error }) => {
        if (error) { setDirectOkunamadi(true); return; }
        setDirectler(directListesi(data?.[0]?.direct_to));
      });
  }, [plan?.id]); // eslint-disable-line

  const getLog = useCallback((action) => {
    const found = logs.filter(l => l.action === action);
    return found.length ? found[found.length-1].details : null;
  }, [logs]);

  // VERI KAYNAGI: flt_report birincil, flight_logs fallback (eski web ucuslari)
  const FR = fltReport;

  // efb-documents bucket PRIVATE -> signed URL (belgeler + imzalar)
  useEffect(() => {
    if (!FR) return;
    const paths = [];
    (Array.isArray(FR.documents) ? FR.documents : []).forEach(d => { if (d && d.file_path) paths.push(d.file_path); });
    if (FR.mandatory && FR.mandatory.signature_url) paths.push(FR.mandatory.signature_url);
    if (FR.accept && FR.accept.signature_url) paths.push(FR.accept.signature_url);
    if (!paths.length) return;
    // 21 Eki 2026 (W4b): bağlantı üretilemezse bunu söyler (eskiden `.catch(() => {})` — imza ve belgeler yokmuş gibi).
    supabase.storage.from('efb-documents').createSignedUrls(paths, 3600)
      .then(({ data, error }) => {
        if (error) { setLinkHatasi(error.message || String(error)); return; }
        setLinkHatasi(null);
        const map = {};
        (data || []).forEach(r => { if (r && r.path && r.signedUrl) map[r.path] = r.signedUrl; });
        setSignedUrls(map);
      }, (e) => setLinkHatasi(e?.message || String(e)));
  }, [FR]);

  const numLb = (v) => {
    if (v === null || v === undefined || v === '' || v === '\u2014') return null;
    const n = parseInt(String(v).replace(/,/g,''), 10);
    return Number.isFinite(n) ? n : null;
  };
  const pick = (a, b) => (a !== null && a !== undefined && a !== '') ? a : (b || '\u2014');
  /** Sayi + birim ('15000' \u2192 '15,000 lb'); bos/okunamayan \u2192 '\u2014'. PDF'teki fmtLb/fmtLt'nin karsiligi. */
  const sayiBirim = (v, birim) => { const n = numLb(v); return n !== null ? `${n.toLocaleString('en-US')} ${birim}` : '\u2014'; };
  const crewLog = getLog('CREW_ASSIGNED');
  const pfName  = pick(FR?.pf_name, crewLog?.pf_name || plan.pf_pilot);
  const pmName  = pick(FR?.pm_name, crewLog?.pm_name || plan.pm_pilot);
  const sigName = (id) => {
    if (!id) return '—';
    if (id === FR?.crew?.pf?.id) return FR.crew.pf.name || id;
    if (id === FR?.crew?.pm?.id) return FR.crew.pm.name || id;
    return id;
  };
  const pfHB    = FR?.crew?.pf?.home_base || pfHomeBase;
  const pmHB    = FR?.crew?.pm?.home_base || pmHomeBase;
  const depIcao      = FR?.dep_icao  || plan.dep;
  const destIcao     = FR?.dest_icao || plan.dest;
  const offBlock = pick(FR?.off_block,    getLog('OFF_BLOCKS')?.time);
  const toTime   = pick(FR?.takeoff_time, getLog('TAKEOFF')?.time);
  const landTime = pick(FR?.landing_time, getLog('LANDING')?.time);
  const onBlock  = pick(FR?.on_block,     getLog('ON_BLOCKS')?.time);
  const toFuelN  = numLb(FR?.takeoff_fuel)   ?? numLb(getLog('TAKEOFF')?.fuel_lb);
  const remFuelN = numLb(FR?.remaining_fuel) ?? numLb(getLog('FUEL_REMAINING')?.fuel_lb);
  const toFuel   = toFuelN  !== null ? toFuelN  : '\u2014';
  const remFuel  = remFuelN !== null ? remFuelN : '\u2014';
  const blockTime  = (FR?.block_minutes    ?? null) !== null ? FR.block_minutes    : diffMins(offBlock, onBlock);
  const flightTime = (FR?.airborne_minutes ?? null) !== null ? FR.airborne_minutes : diffMins(toTime, landTime);
  /** Gece/gunduz — PDF'in `gunGece`i ile ayni metin (2 Kas 2026: rozet yerine kendi hucresi). null = olculemedi → cizgi. */
  const gunGece = (x) => x === true ? 'NIGHT' : x === false ? 'DAY' : '—';
  const tripBurn   = (toFuelN !== null && remFuelN !== null) ? toFuelN - remFuelN : null;
  const planBurn   = numLb(FR?.fuel?.plan_trip) ?? numLb(plan?.trip_fuel);
  const burnDiff   = (tripBurn !== null && planBurn !== null) ? planBurn - tripBurn : null;

  // ── KAYITLI FTL (hesap YOK) ────────────────────────────────────────
  // Gorev kaydi arsivlenirken `archive-flight` tarafindan yazildi; ekran onu
  // yalnizca gosterir. Ayni kaydi FTL paneli de okur, bu yuzden ikisi
  // ayrisamaz.
  const dutyOf = (pilotId) => duties.find(d => d.pilot_id === pilotId) || null;
  const utcHM = (iso) => {
    if (!iso) return '\u2014';
    const d = new Date(iso);
    return String(d.getUTCHours()).padStart(2,'0') + ':' + String(d.getUTCMinutes()).padStart(2,'0');
  };

  // Pilot kimlikleri CREW_ASSIGNED izinden gelir (isim degil ID — gorev kaydi
  // pilot_id ile eslesir). Iz yoksa kayit da eslesmez ve ekran "NO DUTY
  // RECORD" der; tahmin uretilmez.
  const pfDuty = dutyOf(crewLog?.pf_id);
  const pmDuty = dutyOf(crewLog?.pm_id);

  // ---- RAPOR PDF: sunucuda uretilir (archive-flight). Web + iOS AYNI dosya. ----
  const [pdfBusy, setPdfBusy] = useState(false);

  // TEK RAPOR ILKESI (28 Tem): once arsiv PDF'i denenir — iOS/Statistics/Archived FLTs
  // ayni dosyayi gosterir. PDF varsa uygulama ici onizleyici (PRINT/DOWNLOAD/CLOSE);
  // yoksa (cok eski web ucuslari) asagidaki yeniden-insa gorunumune dusulur.
  // undefined = araniyor, null = PDF yok (legacy), {url,name} = hazir
  const [pdfView, setPdfView] = useState(undefined);
  // 🔴 21 Eki 2026 (W4b): PDF aranamaz, bağlantısı üretilemez ya da indirilemezse "PDF YOK" sayılıp eski uçuşların
  //    yeniden kurulan görünümüne SESSİZCE düşülüyordu — asıl rapor varken başka bir şey gösteriliyordu. Artık yalnız
  //    PDF GERÇEKTEN yoksa o görünüm; okunamadıysa kutu + RETRY.
  const [pdfHatasi, setPdfHatasi] = useState(null);
  const [pdfDeneme, setPdfDeneme] = useState(0);
  useEffect(() => {
    let revoke = null;
    setPdfView(undefined); setPdfHatasi(null);
    (async () => {
      try {
        const { data: rows, error: aramaHatasi } = await supabase
          .from('efb_documents')
          .select('file_name,file_path')
          .eq('plan_id', plan.id)
          .eq('section', 'REPORT')
          .order('uploaded_at', { ascending: false })
          .limit(1);
        if (aramaHatasi) { setPdfHatasi(aramaHatasi.message || String(aramaHatasi)); return; }
        if (!rows?.length) { setPdfView(null); return; }
        const { data: signed, error } = await supabase.storage
          .from('efb-documents').createSignedUrl(rows[0].file_path, 3600);
        if (error || !signed?.signedUrl) { setPdfHatasi(error?.message || 'no download link was issued'); return; }
        const resp = await fetch(signed.signedUrl);
        if (!resp.ok) { setPdfHatasi(`download failed (HTTP ${resp.status})`); return; }
        const url = URL.createObjectURL(await resp.blob());
        revoke = url;
        setPdfView({ url, name: rows[0].file_name || 'GO2_FltReport.pdf' });
      } catch (e) { setPdfHatasi(e?.message || String(e)); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [plan.id, pdfDeneme]); // eslint-disable-line

  const downloadPdfView = () => {
    if (!pdfView) return;
    const a = document.createElement('a');
    a.href = pdfView.url; a.download = pdfView.name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // DUZELTME KATMANI (28 Tem): kayit admin tarafindan edit edildiyse rapor
  // goruntusunde eski deger KIRMIZI USTU CIZILI + yaninda NEW rozetiyle yeni
  // deger gosterilir (kagit logbook duzeltme gelenegi). Orijinal PDF DEGISMEZ —
  // admin_edits'ten goruntuleme katmaninda bindirilir.

  const openReportPdf = async (mode = 'download') => {
    setPdfBusy(true);
    const w = window.open('', '_blank');
    try {
      const bag = await raporPdfBaglantisi(plan.id, mode);
      if (!bag) {
        if (w) w.close();
        alert('No report PDF on file. It is produced automatically when the flight is archived.');
        return;
      }
      if (w) w.location.href = bag.url;
      else window.location.href = bag.url;
    } catch (e) {
      if (w) w.close();
      alert('Report PDF could not be opened: ' + (e?.message || e));
    } finally {
      setPdfBusy(false);
    }
  };

  const S = {
    overlay:{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.85)',zIndex:200,overflowY:'auto'},
    wrap:{maxWidth:760,margin:'0 auto',padding:'20px 16px 40px',fontFamily:'monospace'},
    card:{background:'#fff',borderRadius:8,border:'1px solid #e2e8f0',marginBottom:10,overflow:'hidden'},
    // 🔑 BUYUK HARF KURALI (Serkan, 4 Eyl 2026: *"raporlarda kucuk harf var,
    //    biz hep buyuk harf demistik"*). Kural TEK YERDE, stilde uygulanir:
    //    24 etiketi elle duzeltmek yarin yazilacak 25inciyi kurtarmazdi.
    hdr:{background:'#f8fafc',padding:'6px 14px',fontSize:10,fontWeight:700,color:'#64748b',letterSpacing:0.5,borderBottom:'1px solid #e2e8f0',textTransform:'uppercase'},
    grid:(cols)=>({display:'grid',gridTemplateColumns:cols,gap:0}),
    cell:(br,bt)=>({padding:'8px 14px',borderRight:br?'1px solid #e2e8f0':'none',borderTop:bt?'1px solid #e2e8f0':'none'}),
    lbl:{fontSize:9,color:'#94a3b8',marginBottom:2,textTransform:'uppercase'},
    val:{fontSize:12,fontWeight:700,color:'#1e293b',textTransform:'uppercase'},
    chk:(on)=>({width:18,height:18,border:on?'3px solid #1e293b':'2px solid #cbd5e1',borderRadius:3,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:on?'#1e293b':'transparent'}),
  };
  // 🔑 "A DILI" (2 Kas 2026, Serkan — ornek PDF'le onayli): bir satir = bir grup, PDF'in `subHeader` + `cellRow`unun
  //    karsiligi. Renkler PDF'in `C.wpt / C.dep / C.dest / C.divert`i.
  const altBaslik = (ad, renk, rozet, kalin) => (
    <div style={{background:'#f8fafc',padding:'4px 14px',borderTop:`${kalin?2:1}px solid #e2e8f0`}}>
      <span style={{fontSize:9,fontWeight:700,color:renk}}>{ad}</span>
      {rozet && <span style={{fontSize:9,color:'#dc2626',marginLeft:8,fontWeight:700}}>{rozet}</span>}
    </div>);
  const grupSatiri = (hucreler, testId) => (
    <div style={S.grid(`repeat(${hucreler.length}, 1fr)`)} data-testid={testId}>
      {hucreler.map((h, i) => (
        <div key={h.lbl} style={S.cell(i < hucreler.length - 1, true)}>
          <div style={S.lbl}>{h.lbl}</div><div style={{...S.val, ...h.stil}}>{h.val}</div>
        </div>))}
    </div>);
  // Ucusun DONMUS anahtarlari — PDF'in `on(k)`u ile ayni kural; kapali hucre raporda HIC yok.
  const acik = (k) => FR?.features_snapshot?.[k] !== false;

  usePencereKapat(onClose);   // rapor penceresi — yükleniyor ekranı dahil ESC kapatır
  // TEK RAPOR: arsiv PDF'i araniyor / bulundu ise onizleyici (legacy gorunume hic girilmez)
  if (pdfHatasi) return (
    <div style={S.overlay}><div style={S.wrap}>
      <div style={{display:'flex',justifyContent:'flex-end'}}><KapatX onClose={onClose} style={{border:'1px solid #475569',color:'#94a3b8',width:36,height:36}} /></div>
      <OkumaHatasi testid="rapor-pdf-okuma-hatasi" style={{padding:'16px 0'}} satirlar={[{ ad: 'REPORT PDF (efb_documents)', metin: pdfHatasi }]}
        onRetry={() => setPdfDeneme(n => n + 1)} baslik="THE REPORT PDF COULD NOT BE LOADED — this does not mean the flight has no report." />
    </div></div>);
  if (pdfView === undefined) return <div style={S.overlay}><div style={{...S.wrap,color:'#94a3b8',textAlign:'center',paddingTop:80}}>LOADING REPORT...</div></div>;
  if (pdfView) return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:200,display:'flex',flexDirection:'column'}}>
      <div style={{background:'#0f172a',borderBottom:'1px solid #334155',padding:'10px 16px',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,color:'#fff'}}>GO2 eFB — FLIGHT REPORT</div>
          <div style={{fontSize:10,color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{plan.reg} · {plan.dep}–{plan.dest} · {plan.date} · {pdfView.name}</div>
        </div>
        <button onClick={()=>{const f=document.getElementById('flt-report-pdf-frame');try{f?.contentWindow?.print();}catch{window.print();}}}
          style={{background:'#1e40af',border:'none',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:700,color:'#fff',cursor:'pointer'}}>🖨 Print</button>
        <button onClick={downloadPdfView}
          style={{background:'none',border:'1px solid #1e40af',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:700,color:'#60a5fa',cursor:'pointer'}}>⬇ Download</button>
        <KapatX onClose={onClose} style={{border:'1px solid #475569',color:'#94a3b8'}} />
      </div>
      {amendments.length > 0 && (
        <div style={{background:'#2a0e0e',borderBottom:'2px solid #ef4444',padding:'8px 16px',flexShrink:0,maxHeight:160,overflowY:'auto'}}>
          <div style={{fontSize:11,fontWeight:700,color:'#ef4444',letterSpacing:1.5,marginBottom:6}}>
            ⚠ AMENDED RECORD — {amendments.length} CORRECTION(S) AFTER ARCHIVING · original values struck through
          </div>
          {amendments.map((a2, i) => (
            <div key={i} style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap',padding:'3px 0',fontFamily:'monospace',fontSize:12}}>
              <span style={{color:'#fca5a5',fontWeight:700,textTransform:'uppercase'}}>{a2.field_name}</span>
              <span style={{color:'#ef4444',textDecoration:'line-through',textDecorationColor:'#ef4444',textDecorationThickness:2}}>{a2.old_value || '—'}</span>
              <span style={{color:'#94a3b8'}}>→</span>
              <span style={{background:'#14532d',color:'#4ade80',fontWeight:700,padding:'1px 8px',borderRadius:4,fontSize:11}}>NEW</span>
              <span style={{color:'#4ade80',fontWeight:700}}>{a2.new_value || '—'}</span>
              <span style={{color:'#64748b',fontSize:10}}>· {a2.reason || '—'} · {utcGunSaat(a2.created_at,{z:true})}</span>
            </div>
          ))}
        </div>
      )}
      <iframe id="flt-report-pdf-frame" title="GO2 Flight Report" src={pdfView.url} style={{flex:1,border:'none',background:'#525659'}}/>
    </div>
  );

  if(loading) return <div style={S.overlay}><div style={{...S.wrap,color:'#94a3b8',textAlign:'center',paddingTop:80}}>Loading report...</div></div>;
  if(raporHatasi) return (
    <div style={S.overlay}><div style={S.wrap}>
      <div style={{display:'flex',justifyContent:'flex-end'}}><KapatX onClose={onClose} style={{border:'1px solid #475569',color:'#94a3b8',width:36,height:36}} /></div>
      <OkumaHatasi testid="rapor-okuma-hatasi" style={{padding:'16px 0'}} satirlar={raporHatasi} onRetry={() => setRaporDeneme(n => n + 1)}
        baslik="REPORT COULD NOT BE LOADED — it is not shown with part of its data." />
    </div></div>);

  return (
    <>
    <style dangerouslySetInnerHTML={{__html: "@media print { .no-print { display:none!important; } #flt-report-wrap { position:static!important; overflow:visible!important; height:auto!important; background:#fff!important; } #flt-report-wrap * { color:#000!important; } }"}} />
    <div style={S.overlay} id="flt-report-wrap">
      <div style={S.wrap} id="flt-report-content">
        {linkHatasi && (
          <div className="no-print" data-testid="rapor-baglanti-hatasi" style={{color:'#f59e0b',fontSize:11,marginBottom:8,fontFamily:'var(--mono)'}}>
            SIGNATURES AND DOCUMENTS COULD NOT BE LINKED ({linkHatasi}) — they cannot be shown now; this does not mean they are
            missing. Close and reopen the report to try again.
          </div>)}
        {/* Toolbar */}
        <div className="no-print" style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:'#fff'}}>GO2 eFB — Flight Report</div>
            <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{plan.reg} · {plan.dep}–{plan.dest} · {plan.date}</div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>openReportPdf('print')} disabled={pdfBusy}  style={{background:'rgba(255,255,255,0.1)',border:'1px solid #475569',borderRadius:6,padding:'8px 16px',fontSize:12,color:'#fff',cursor:'pointer'}}>🖨 Print</button>
            <button onClick={()=>openReportPdf('download')} disabled={pdfBusy} style={{background:pdfBusy?'#334155':'#1e40af',border:'1px solid #1e40af',borderRadius:6,padding:'8px 16px',fontSize:12,color:'#fff',cursor:pdfBusy?'wait':'pointer'}}>{pdfBusy?'… PDF':'⬇ Save PDF'}</button>
            <KapatX onClose={onClose} style={{border:'1px solid #475569',color:'#94a3b8',width:36,height:36}} />
          </div>
        </div>

        {/* Aircraft & Crew */}
        <div style={S.card}>
          <div style={S.hdr}>AIRCRAFT & CREW</div>
          <div style={S.grid('1fr 1fr 1fr')}>
            <div style={S.cell(true,false)}><div style={S.lbl}>Registration</div><div style={S.val}>{plan.reg}</div></div>
            <div style={S.cell(true,false)}><div style={S.lbl}>Type</div><div style={S.val}>{plan.ac_type}</div></div>
            <div style={S.cell(false,false)}><div style={S.lbl}>Date</div><div style={S.val}>{plan.date}</div></div>
            <div style={S.cell(true,true)}><div style={S.lbl}>PIC (PF)</div><div style={S.val}>{pfName}</div></div>
            <div style={S.cell(true,true)}><div style={S.lbl}>SIC (PM)</div><div style={S.val}>{pmName}</div></div>
            {/* PAX (2 Kas 2026, Serkan "PDF'le birebir (A)"): PDF'teki yerinde. Eskiden burada her zaman PIC'i tekrar eden
                "Pre-flight by" vardi, PAX FLIGHT DATA'daydi. */}
            <div style={S.cell(false,true)}><div style={S.lbl}>Pax</div><div style={S.val}>{fltReport?.pax ?? '—'}</div></div>
          </div>
        </div>

        {/* Flight Data */}
        {/* 🔑 DIZILIM (2 Kas 2026, Serkan — "A dili", ornek PDF'le onayli): PDF'le (`report-pdf.ts`) ayni dort grup.
            *"off block on block block time, take off landing flt time bir grup olmali — bakinca direkt gorebilmeli
            saatleri"*. PDF'le BIREBIR (Serkan "A"): eskiden web'e ozgu STA kalkti, PAX AIRCRAFT & CREW'a cikti. Plana gore
            DELAY/EARLY notu yalniz PDF'te — bu yedek gorunum hesap yapmaz. Divert'te ROUTE basligi kirmizi + DIVERT. */}
        <div style={S.card}>
          <div style={S.hdr}>FLIGHT DATA</div>
          {altBaslik('ROUTE', FR?.is_divert ? '#dc2626' : '#1e40af', FR?.is_divert ? 'DIVERT' : null)}
          {grupSatiri([
            { lbl: 'Departure', val: depIcao || '—' },
            { lbl: 'Destination', val: destIcao || '—' },
            { lbl: 'Alternate', val: plan.alternate || '—' },
            { lbl: 'STD (Plan)', val: birimli(plan.std, 'UTC') },
          ], 'rapor-route')}
          {altBaslik('BLOCK', '#1e40af')}
          {grupSatiri([
            { lbl: 'Off Block', val: birimli(offBlock, 'UTC') },
            { lbl: 'On Block', val: birimli(onBlock, 'UTC') },
            { lbl: 'Block Time', val: sureMetni(blockTime) },
          ], 'rapor-block')}
          {altBaslik('FLIGHT', '#1e40af')}
          {grupSatiri([
            { lbl: 'Take Off', val: birimli(toTime, 'UTC') },
            { lbl: 'Landing', val: birimli(landTime, 'UTC') },
            { lbl: 'Flight Time', val: sureMetni(flightTime) },
          ], 'rapor-flight')}
          {/* 🔑 Serkan (4 Eyl 2026): *"Take off gece landing gunduz mesela onu gosteriyor mu?"* Deger arsiv kaydindan
              gelir (`archive-flight` SHT-FCL Md.4/r ile olcer); burada YENIDEN HESAPLANMAZ — tek kaynak (Ilke 3).
              ⚠️ NULL "GUNDUZ" DEMEZ: olculemediyse cizgi konur. LANDINGS (END FLT "CYCLES") kayittan; 1 VARSAYILMAZ
              (Ilke 1). TYPE OF LNDG (22 Eyl 2026) PDF ile ayni kayittan. */}
          {altBaslik('DAY-NIGHT & LANDINGS', '#1e40af')}
          {grupSatiri([
            { lbl: 'T/O Day-Night', val: gunGece(af?.is_night_takeoff) },
            { lbl: 'LDG Day-Night', val: gunGece(af?.is_night_landing) },
            { lbl: 'Landings', val: af?.landing_count ?? '—' },
            { lbl: 'Type of LNDG', val: af?.lnd_type || '—' },
          ], 'rapor-gece-inis')}
        </div>

        {/* Fuel */}
        <div style={S.card}>
          <div style={S.hdr}>FUEL</div>
          {/* SIRA (12 Eki 2026, Serkan) — PDF ile aynı on iki hücre, aynı sıra; türetilenler `ofpYakit.js`ten. */}
          {(() => {
            const oncesi = yakitAlimOncesi(FR?.fuel?.fob, FR?.fuel?.uplift_lb);
            const upliftYok = !(numLb(FR?.fuel?.uplift_lb) > 0);
            const upliftVar = [FR?.fuel?.uplift_lt, FR?.fuel?.uplift_lb].some(v => v !== null && v !== undefined && v !== '');
            const ekstra = asgariUstuYakit(toFuel !== '—' ? toFuel : null, minTo);
            const not = (t) => <div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{t}</div>;
            const isaretli = (v) => (v > 0 ? '+' : '') + v.toLocaleString('en-US') + ' lb';
            return (<>
              <div style={S.grid('1fr 1fr 1fr 1fr')}>
                <div style={S.cell(true,false)}><div style={S.lbl}>Plan FOB</div><div style={S.val}>{sayiBirim(FR?.fuel?.plan_fob ?? plan.fob,'lb')}</div></div>
                <div style={S.cell(true,false)}><div style={S.lbl}>Remaining (before uplift)</div><div style={S.val}>{sayiBirim(oncesi,'lb')}</div>{oncesi !== null && upliftYok && not('no uplift — = FOB')}</div>
                <div style={S.cell(true,false)}><div style={S.lbl}>Uplift (Lt / Lb)</div><div style={S.val}>{upliftVar ? `${sayiBirim(FR?.fuel?.uplift_lt,'lt')} / ${sayiBirim(FR?.fuel?.uplift_lb,'lb')}` : '—'}</div></div>
                <div style={S.cell(false,false)}><div style={S.lbl}>Density (kg/lt)</div><div style={S.val}>{FR?.fuel?.density||'—'}</div></div>
              </div>
              <div style={S.grid('1fr 1fr 1fr 1fr')}>
                <div style={S.cell(true,true)}><div style={S.lbl}>Actual FOB</div><div style={S.val}>{sayiBirim(FR?.fuel?.fob,'lb')}</div></div>
                <div style={S.cell(true,true)}><div style={S.lbl}>T/O Fuel</div><div style={S.val}>{sayiBirim(toFuel !== '—' ? toFuel : null,'lb')}</div></div>
                <div style={S.cell(true,true)}><div style={S.lbl}>Extra fuel (vs MIN T/O)</div><div style={{...S.val,color:ekstra===null?'#1e293b':ekstra>=0?'#16a34a':'#ef4444'}}>{ekstra!==null?isaretli(ekstra):'—'}</div>{not(minTo!==null?`MIN T/O ${minTo.toLocaleString('en-US')} lb (OFP)`:'MIN T/O not in OFP')}</div>
                <div style={S.cell(false,true)}><div style={S.lbl}>Trip Burn</div><div style={S.val}>{tripBurn!==null?tripBurn.toLocaleString('en-US')+' lb':'—'}</div></div>
              </div>
              <div style={S.grid('1fr 1fr 1fr 1fr')}>
                <div style={S.cell(true,true)}><div style={S.lbl}>Remaining (after landing)</div><div style={S.val}>{sayiBirim(remFuel !== '—' ? remFuel : null,'lb')}</div></div>
                <div style={S.cell(true,true)}><div style={S.lbl}>Alternate (Plan)</div><div style={S.val}>{sayiBirim(FR?.fuel?.plan_alternate ?? plan?.alternate_fuel,'lb')}</div></div>
                <div style={S.cell(true,true)}><div style={S.lbl}>Final Reserve (Plan)</div><div style={S.val}>{sayiBirim(FR?.fuel?.plan_reserve ?? plan?.reserve_fuel,'lb')}</div></div>
                <div style={S.cell(false,true)}><div style={S.lbl}>Trip diff vs OFP</div><div style={{...S.val,color:burnDiff===null?'#1e293b':burnDiff>0?'#16a34a':'#ef4444'}}>{burnDiff!==null?isaretli(burnDiff):'—'}</div></div>
              </div>
            </>);
          })()}
        </div>

        {/* NAV LOG — flt_report.navlog'dan */}
        {(()=>{
          const navlogData = fltReport?.navlog || [];
          // Divert noktasindan SONRAKI planli WPT'ler uculmadi (dest + alt dahil)
          const divertIdx = navlogData.findIndex(r => r.type === 'divert-arpt');
          const rows = navlogData.map((row, idx) => {
            const notFlown = divertIdx >= 0 && idx > divertIdx;
            const isDiv    = row.type === 'divert-arpt';
            const isPlt    = row.custom === true && !isDiv;
            return {
              wpt: row.wpt,
              type: row.type?.toUpperCase(),
              eta: (row.eta && row.eta !== '—') ? row.eta : '—',
              // G = GPS'in yazdigi saat (PDF ve iPad ile ayni isaret, 22 Eyl 2026).
              ata: row.ata ? ((row.ata_src === 'GPS' ? 'G ' : '') + row.ata) : '—',
              fuel: row.fuel_actual ? parseInt(row.fuel_actual).toLocaleString()+' lb' : '—',
              // RVSM iPad satırı gibi (12 Eki 2026, birim): kayıt "pri1/sby/pri2" → "PRI1 … SBY … PRI2 … ft".
              rvsm: (() => { const r = String(row.rvsm || '').trim(); const p = r.split('/');
                return !r ? '—' : p.length === 3 ? `PRI1 ${p[0]}  SBY ${p[1]}  PRI2 ${p[2]} ft` : birimli(r, 'ft'); })(),
              notFlown, isDiv, isPlt,
              bg: notFlown ? '#f8fafc'
                : isDiv    ? '#fef2f2'
                : isPlt    ? '#faf5ff'
                : row.type==='dep'  ? '#fef9ec'
                : row.type==='dest' ? '#f0fdf4' : '#fff',
              color: isDiv ? '#dc2626'
                : isPlt    ? '#7c3aed'
                : row.type==='dep'  ? '#b45309'
                : row.type==='dest' ? '#166534' : '#1e40af',
            };
          });
          if(!rows.length) return null;
          return (
            <div style={S.card}>
              <div style={S.hdr}>NAV LOG — Actual Times & Fuel</div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11,fontFamily:'monospace'}}>
                  <thead>
                    <tr style={{background:'#f1f5f9'}}>
                      {['WPT','TYPE','ETA (UTC)','ATA (UTC)','FUEL','RVSM'].map(h=>(
                        <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:9,color:'#64748b',fontWeight:700,borderBottom:'1px solid #e2e8f0',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row,i)=>(
                      <tr key={i} style={{background:row.bg,opacity:row.notFlown?0.4:1}}>
                        <td style={{padding:'6px 10px',fontWeight:700,color:row.color,borderBottom:'1px solid #f1f5f9',textDecoration:row.notFlown?'line-through':'none'}}>
                          {row.wpt}
                          {row.isDiv && <span style={{marginLeft:6,fontSize:8,fontWeight:700,color:'#dc2626',border:'1px solid #dc2626',borderRadius:3,padding:'1px 3px'}}>DIVERT</span>}
                          {row.isPlt && <span style={{marginLeft:6,fontSize:8,fontWeight:700,color:'#7c3aed',border:'1px solid #7c3aed',borderRadius:3,padding:'1px 3px'}}>+PLT</span>}
                        </td>
                        <td style={{padding:'6px 10px',color:'#94a3b8',fontSize:9,borderBottom:'1px solid #f1f5f9'}}>{row.notFlown?'NOT FLOWN':row.type}</td>
                        <td style={{padding:'6px 10px',color:'#94a3b8',borderBottom:'1px solid #f1f5f9'}}>{row.eta}</td>
                        <td style={{padding:'6px 10px',fontWeight:700,color:'#1e293b',borderBottom:'1px solid #f1f5f9'}}>{row.ata}</td>
                        <td style={{padding:'6px 10px',color:'#1e293b',borderBottom:'1px solid #f1f5f9'}}>{row.fuel}</td>
                        <td style={{padding:'6px 10px',color:'#64748b',fontSize:10,borderBottom:'1px solid #f1f5f9',fontFamily:'monospace'}}>{row.rvsm}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* DIRECT TO — ekibin verdigi her direct, iptal edilenler dahil (22 Eyl 2026).
            Serkan: "Her direct-to raporda yazilmali." PDF ile ayni liste. */}
        {(directler.length > 0 || directOkunamadi) && (
          <div style={S.card}>
            <div style={S.hdr}>DIRECT TO — crew entries</div>
            {directOkunamadi ? (
              <div style={{padding:'8px 14px',fontSize:11,color:'#b45309'}}>THE DIRECT-TO LIST COULD NOT BE READ.</div>
            ) : directler.map((d, i) => (
              <div key={i} style={{display:'flex',gap:16,padding:'5px 14px',borderTop:i?'1px solid #f1f5f9':'none',fontFamily:'monospace',fontSize:11}}>
                <span style={{fontWeight:700,color:d.cancelled_at?'#94a3b8':'#b45309',minWidth:220}}>{saatZ(d.set_at)}  {d.from} → {d.to}</span>
                <span style={{color:d.cancelled_at?'#dc2626':'#64748b'}}>
                  {d.cancelled_at ? `CANCELLED ${saatZ(d.cancelled_at)}`
                    : d.skipped != null ? `${d.skipped} POINT${d.skipped === 1 ? '' : 'S'} SKIPPED` : ''}
                </span>
              </div>
            ))}
            {!directOkunamadi && directler.some(d => d.set_at == null) && (
              <div style={{padding:'4px 14px 8px',fontSize:9,color:'#94a3b8'}}>
                Recorded by an app version that kept only the LAST direct and no times — earlier directs of this flight are in the audit log (DIRECT_TO_SET).
              </div>
            )}
          </div>
        )}

        {/* T/O & LANDING */}
        {(FR?.takeoff || FR?.landing) && (
          <div style={S.card}>
            <div style={S.hdr}>T/O &amp; LANDING DATA</div>
            {/* 🔑 DIZILIM (2 Kas 2026, Serkan — "A dili"): *"suratler perf girdisinden geliyor, bir arada olmali"*. Satirlar
                iPad T/O DATA / LND DATA ekranlarinin bolum sirasini izler, PDF (`report-pdf.ts`) ile AYNI sira; mevcut pist
                (ASDA / LDA) ile gereken (REQ RW / REQ LND) yan yana; ATIS tam genislik. */}
            {FR?.takeoff && (<>
              {altBaslik(`TAKEOFF — ${FR.takeoff.icao||depIcao}`, '#b45309')}
              {grupSatiri([{ lbl: 'ATIS', val: FR.takeoff.atis || '—', stil: { whiteSpace: 'normal' } }], 'rapor-tk-atis')}
              {grupSatiri([
                { lbl: 'RWY', val: FR.takeoff.rwy || '—' },
                { lbl: 'ASDA', val: birimli(FR.takeoff.rwy_len, 'ft', { binlik: true }) },
                { lbl: 'REQ RW', val: birimli(FR.takeoff.req_rw, 'ft', { binlik: true }) },
              ], 'rapor-tk-pist')}
              {/* TOW / ZFW / LW: PLAN + LMC CORRECTED (31 Eki 2026, Serkan — G7) — PDF ile aynı kural
                  (`raporAgirlikSatirlari`); kayıt yoksa (Build 77 öncesi) satır hiç yok. */}
              {(() => {
                const ag = raporAgirlikSatirlari(FR.takeoff.agirlik);
                if (!ag) return null;
                const satir = (hucreler, testId) =>
                  grupSatiri(hucreler.map(h => ({ lbl: h.lbl, val: birimli(h.deger, 'lb', { binlik: true }) })), testId);
                return <>{satir(ag.plan, 'rapor-agirlik-plan')}{ag.lmc && satir(ag.lmc, 'rapor-agirlik-lmc')}</>;
              })()}
              {/* LMC: FUEL / PAYLOAD / NET (12 Eki 2026, Serkan) — PDF ile aynı satır. Eski tek LMC
                  türü bilinmez: "LMC (type not recorded)". Anahtar kapalıysa satır hiç yok. */}
              {FR.features_snapshot?.['ui.takeoff.lmc'] !== false && (() => {
                const yeniVar = lmcMetni(FR.takeoff.lmc_fuel) !== null || lmcMetni(FR.takeoff.lmc_payload) !== null;
                const eski = lmcMetni(FR.takeoff.lmc);
                return (!yeniVar && eski !== null) ? (
                  <div style={S.grid('1fr')}>
                    <div style={S.cell(false,true)}><div style={S.lbl}>LMC (type not recorded)</div><div style={S.val}>{eski}</div><div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>entered on an app version without the FUEL / PAYLOAD split</div></div>
                  </div>
                ) : (
                  <div style={S.grid('1fr 1fr 1fr')}>
                    <div style={S.cell(true,true)}><div style={S.lbl}>Fuel LMC</div><div style={S.val}>{lmcMetni(FR.takeoff.lmc_fuel) ?? '\u2014'}</div></div>
                    <div style={S.cell(true,true)}><div style={S.lbl}>Payload LMC</div><div style={S.val}>{lmcMetni(FR.takeoff.lmc_payload) ?? '\u2014'}</div></div>
                    <div style={S.cell(false,true)}><div style={S.lbl}>Net LMC</div><div style={S.val}>{lmcNetMetni(FR.takeoff.lmc_fuel, FR.takeoff.lmc_payload) ?? '\u2014'}</div></div>
                  </div>
                );
              })()}
              {/* PERFORMANCE SPEEDS — iPad'deki sirayla; VSE suratlerin yaninda (Serkan: *"VSE neden baska yerde?"*). */}
              {grupSatiri([
                { lbl: 'V1', val: birimli(FR.takeoff.v1, 'kt') },
                { lbl: 'VR', val: birimli(FR.takeoff.vr, 'kt') },
                { lbl: 'V2', val: birimli(FR.takeoff.v2, 'kt') },
                { lbl: 'VSE', val: birimli(FR.takeoff.vse, 'kt') },
                { lbl: 'TRIM', val: birimli(FR.takeoff.trim, '°') },
              ], 'rapor-tk-hiz')}
              {/* ATC CLEARANCE — 31 Eki 2026 (G9): Init FL → FL/ALT, ATC Other → ATC RWY (PDF ile ayni ad; ustteki RWY
                  performans pisti). */}
              {grupSatiri([
                { lbl: 'SID', val: FR.takeoff.sid || '—' },
                { lbl: 'FL/ALT', val: FR.takeoff.fl || '—' },
                { lbl: 'Squawk', val: FR.takeoff.sq || '—' },
                { lbl: 'ATC RWY', val: FR.takeoff.oth || '—' },
              ], 'rapor-tk-atc')}
              {grupSatiri([{ lbl: 'RVSM (P1/SBY/P2)', stil: { fontSize: 10 },
                val: [FR.takeoff.rvsm?.pri1,FR.takeoff.rvsm?.sby,FR.takeoff.rvsm?.pri2].some(x=>x)?[FR.takeoff.rvsm?.pri1,FR.takeoff.rvsm?.sby,FR.takeoff.rvsm?.pri2].map(x=>x||'—').join(' / ')+' ft':'—' }], 'rapor-tk-rvsm')}
              {/* DE/ANTI-ICING (22 Eyl 2026) — iPad'in urettigi cumle AYNEN, PDF'teki gibi
                  kalin italik. Kayit yoksa satir hic yok. */}
              {FR.takeoff.deice && (
                <div style={{padding:'6px 14px',borderTop:'1px solid #e2e8f0'}}>
                  <div style={S.lbl}>DE/ANTI-ICING</div>
                  <div style={{...S.val,fontWeight:700,fontStyle:'italic',whiteSpace:'normal'}}>{FR.takeoff.deice}</div>
                </div>
              )}
            </>)}
            {FR?.landing && (<>
              {altBaslik(`LANDING — ${FR.landing.icao||destIcao}`, '#166534', FR.landing.is_divert ? 'DIVERT' : null, true)}
              {/* `landing.atis` — `arr_atis` bu blokta HIC yazilmadi (olu okuma, 22 Eyl'de kaldirildi). */}
              {grupSatiri([{ lbl: 'ATIS', val: FR.landing.atis || '—', stil: { whiteSpace: 'normal' } }], 'rapor-ld-atis')}
              {/* ALTERNATE WXR (31 Eki 2026, G8) — PDF'teki gibi: yazı girildiyse tam genişlikte satır, foto PDF'te ek
                  sayfa. Kural tek (`altnWxrSatiri`). */}
              {(() => {
                const altn = altnWxrSatiri(FR.landing.altn_icao, FR.landing.altn_wxr);
                if (!altn) return null;
                return (
                  <div style={{padding:'6px 14px',borderTop:'1px solid #e2e8f0'}} data-testid="rapor-altn-wxr">
                    <div style={S.lbl}>{altn.icao ? `ALTN WXR \u2014 ${altn.icao}` : 'ALTN WXR'}</div>
                    <div style={{...S.val,whiteSpace:'normal'}}>{altn.metin}</div>
                  </div>
                );
              })()}
              {/* QNH · RWY COND — kapali anahtarin hucresi HIC yok (PDF ile ayni kural, 10 Agu 2026). */}
              {(() => {
                const hucreler = [
                  ...(acik('ui.lnddata.qnh') ? [{ lbl: 'QNH', val: birimli(FR.landing.qnh, 'hPa') }] : []),
                  ...(acik('ui.lnddata.rwycond') ? [{ lbl: 'RWY COND', val: FR.landing.rwy_cond || '—' }] : []),
                ];
                return hucreler.length ? grupSatiri(hucreler, 'rapor-ld-qnh') : null;
              })()}
              {grupSatiri([
                { lbl: 'RWY', val: FR.landing.rwy || '—' },
                { lbl: 'LDA', val: birimli(FR.landing.rwy_len, 'ft', { binlik: true }) },
                { lbl: 'REQ LND', val: birimli(FR.landing.req_lnd, 'ft', { binlik: true }) },
              ], 'rapor-ld-pist')}
              {grupSatiri([
                { lbl: 'ACTUAL LW', val: sayiBirim(FR.landing.actual_lw, 'lb') },
                ...(acik('ui.lnddata.vref') ? [{ lbl: 'VREF', val: birimli(FR.landing.vref, 'kt') }] : []),
              ], 'rapor-ld-agirlik')}
            </>)}
          </div>
        )}

        {/* SIGNATURES */}
        {(FR?.mandatory || FR?.accept) && (
          <div style={S.card}>
            <div style={S.hdr}>SIGNATURES</div>
            <div style={S.grid('1fr 1fr')}>
              <div style={S.cell(true,false)}>
                {/* PREFLIGHT SIGN-OFF (2 Kas 2026) — PDF ile ayni ad, iPad MANDATORY imzasinin adi; eskiden "Mandatory Check". */}
                <div style={S.lbl}>PREFLIGHT SIGN-OFF — {sigName(FR?.mandatory?.signed_by)}</div>
                {FR?.mandatory?.signature_url
                  ? <img src={signedUrls[FR.mandatory.signature_url]} alt="mandatory signature" style={{height:44,marginTop:4,background:'#fff'}} />
                  : <div style={S.val}>Not signed</div>}
                <div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{FR?.mandatory?.signed_at ? new Date(FR.mandatory.signed_at).toUTCString() : ''}</div>
              </div>
              <div style={S.cell(false,false)}>
                <div style={S.lbl}>Plan Accepted (PIC) — {FR?.accept?.pic_id ? sigName(FR.accept.pic_id) : pfName}</div>
                {FR?.accept?.signature_url
                  ? <img src={signedUrls[FR.accept.signature_url]} alt="accept signature" style={{height:44,marginTop:4,background:'#fff'}} />
                  : <div style={S.val}>{FR?.accept?.accepted ? 'Accepted' : 'Not signed'}</div>}
                <div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{FR?.accept?.signed_at ? new Date(FR.accept.signed_at).toUTCString() : ''}</div>
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENTS */}
        {Array.isArray(FR?.documents) && FR.documents.length>0 && (
          <div style={S.card}>
            <div style={S.hdr}>DOCUMENTS ({FR.documents.length})</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11,fontFamily:'monospace'}}>
              <thead><tr style={{background:'#f1f5f9'}}>
                {['SECTION','FILE','SIZE','UPLOADED'].map(h=>(<th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:9,color:'#64748b',fontWeight:700,borderBottom:'1px solid #e2e8f0'}}>{h}</th>))}
              </tr></thead>
              <tbody>
                {FR.documents.map((d,i)=>(
                  <tr key={d.id||i}>
                    <td style={{padding:'6px 10px',fontWeight:700,color:'#1e40af',fontSize:10,borderBottom:'1px solid #f1f5f9'}}>{(d.section||'\u2014').toUpperCase()}</td>
                    <td style={{padding:'6px 10px',color:'#1e293b',borderBottom:'1px solid #f1f5f9'}}>
                      <a href={signedUrls[d.file_path]} target="_blank" rel="noopener noreferrer" style={{color:'#1e40af'}}>{d.file_name}</a>
                    </td>
                    <td style={{padding:'6px 10px',color:'#64748b',fontSize:10,borderBottom:'1px solid #f1f5f9'}}>{d.file_size?Math.round(d.file_size/1024)+' KB':'\u2014'}</td>
                    <td style={{padding:'6px 10px',color:'#94a3b8',fontSize:10,borderBottom:'1px solid #f1f5f9'}}>{d.uploaded_at?new Date(d.uploaded_at).toISOString().slice(0,16).replace('T',' '):'\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {FR.documents.filter(d=>(d.mime_type||'').includes('pdf')).map((d,i)=>(
              <div key={'emb'+i} style={{borderTop:'1px solid #e2e8f0'}}>
                <div style={{padding:'6px 14px',background:'#f8fafc',fontSize:9,fontWeight:700,color:'#64748b'}}>{(d.section||'').toUpperCase()} — {d.file_name}</div>
                <iframe
                  title={d.file_name}
                  src={signedUrls[d.file_path]}
                  style={{width:'100%',height:900,border:'none',display:'block',background:'#fff'}}
                />
              </div>
            ))}
          </div>
        )}

        {/* AIRCRAFT / ENGINE HOURS */}
        {FR?.ac_hours && (
          <div style={S.card}>
            <div style={S.hdr}>AIRCRAFT &amp; ENGINE HOURS (after this flight)</div>
            {/* 12 Eki 2026 (Serkan: "tek kaynak"): AIRCRAFTS hesabı, bu uçuşa kadar — PDF ile aynı hücreler ve notlar.
                Eski biçimdeki kopya (tabansız RPC) yalnız yeniden üretilmemiş raporlarda kalır. */}
            {FR.ac_hours.okunamadi ? (
              <div style={S.cell(false,false)}><div style={S.lbl}>Aircraft hours</div><div style={{...S.val,color:'#ef4444'}}>NOT AVAILABLE</div><div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{FR.ac_hours.okunamadi}</div></div>
            ) : Array.isArray(FR.ac_hours.engines) ? (
              <div style={S.grid(`repeat(${FR.ac_hours.engines.length + 2},1fr)`)}>
                <div style={S.cell(true,false)}><div style={S.lbl}>Airframe</div><div style={S.val}>{birimli(FR.ac_hours.airframe,'h')}</div>{FR.ac_hours.airframe_once&&<div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>flight before the aircraft baseline ({FR.ac_hours.airframe_once})</div>}</div>
                {FR.ac_hours.engines.map(e => (
                  <div key={e.ad} style={S.cell(true,false)}><div style={S.lbl}>{e.ad}</div><div style={S.val}>{birimli(e.saat,'h')}</div>
                    <div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{e.notSet ? 'engine baseline not set' : e.once ? `flight before the engine baseline (${e.once})` : [e.sn ? `S/N ${e.sn}` : null, e.dongu != null ? `${e.dongu} CYC` : null].filter(Boolean).join(' · ')}</div></div>
                ))}
                <div style={S.cell(false,false)}><div style={S.lbl}>Cycles</div><div style={S.val}>{FR.ac_hours.cycles ?? '\u2014'}</div></div>
              </div>
            ) : (
            <div style={S.grid('1fr 1fr 1fr 1fr')}>
              <div style={S.cell(true,false)}><div style={S.lbl}>Airframe</div><div style={S.val}>{birimli(FR.ac_hours.airframe,'h')}</div></div>
              <div style={S.cell(true,false)}><div style={S.lbl}>Engine 1</div><div style={S.val}>{birimli(FR.ac_hours.eng1,'h')}</div></div>
              <div style={S.cell(true,false)}><div style={S.lbl}>Engine 2</div><div style={S.val}>{birimli(FR.ac_hours.eng2,'h')}</div></div>
              <div style={S.cell(false,false)}><div style={S.lbl}>Cycles</div><div style={S.val}>{FR.ac_hours.cycles ?? '\u2014'}</div></div>
            </div>
            )}
          </div>
        )}

        {/* GÖREV SAATLERİ (12 Eki 2026, Serkan: *"sadece DT ve FDT ve reporting, end of duty time verelim; regülasyona dair
            dinlenme, rest vs. bilgisi vermeyelim"* · *"SKPK da çıksın — pilotun kafası karışmasın"*). PDF ile aynı dört hücre. */}
        <div style={S.card}>
          <div style={S.hdr}>CREW DUTY TIMES</div>
          {[['PF', pfName, pfHB, pfDuty, '#1e40af'], ['PM', pmName, pmHB, pmDuty, '#0f766e']].map(([rol, ad, hb, gorev, renk]) => (
            <React.Fragment key={rol}>
              <div style={{borderTop:'2px solid #e2e8f0',background:'#f8fafc',padding:'4px 14px'}}><span style={{fontSize:9,fontWeight:700,color:renk}}>{rol} — {ad}</span>{hb?<span style={{fontSize:9,color:'#94a3b8',marginLeft:8}}>Home: {hb}</span>:<span style={{fontSize:9,color:'#ef4444',marginLeft:8}}>No home base set</span>}</div>
              <div style={S.grid('1fr 1fr 1fr 1fr')}>
                {gorev ? (<>
                <div style={S.cell(true,false)}><div style={S.lbl}>Report Time</div><div style={S.val}>{birimli(utcHM(gorev.report_time),'UTC')}</div></div>
                <div style={S.cell(true,false)}><div style={S.lbl}>FDP</div><div style={S.val}>{sureMetni(gorev.fdp_minutes)}</div></div>
                <div style={S.cell(true,false)}><div style={S.lbl}>Duty End</div><div style={S.val}>{birimli(utcHM(gorev.duty_end),'UTC')}</div></div>
                <div style={S.cell(false,false)}><div style={S.lbl}>DT</div><div style={S.val}>{sureMetni(gorevSuresiDk(gorev))}</div></div>
                </>) : (
                <div style={S.cell(false,false)}><div style={S.lbl}>Duty record</div><div style={{...S.val,color:'#b45309'}}>NOT LINKED</div><div style={{fontSize:9,color:'#94a3b8',marginTop:2}}>Not linked to a crew duty — duty times are not shown rather than estimated.</div></div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div style={{fontSize:10,color:'#94a3b8',textAlign:'center',marginTop:8}}>
          Report generated by GO2 eFB · Archive copy · CAMO data not included · {new Date().toUTCString()}
          {/* 🔑 HANGI SURUM BU UCUSU URETTI (31 Agu 2026, K-3): bir hesap
              hatasi bulundugunda etkilenen ucuslar bu satirdan bulunur.
              Eski kayitlarda bos — uydurulmaz, "unknown" yazilir. */}
          <br/>Recorded by: {af?.app_version ? `GO2 eFB ${af.app_version}` : 'version unknown (archived before version tracking)'}
          {af?.ios_version && ` · iOS ${af.ios_version}`}
          {af?.device_model && ` · ${af.device_model}`}
        </div>
      </div>
    </div>
    </>
  );
}